import './style.css';
import { games } from './platform/game-registry';
import type { GameDefinition } from './platform/types';

const appElement = document.querySelector<HTMLElement>('#app');
if (!appElement) throw new Error('App root was not found.');
const app: HTMLElement = appElement;
let disposeGame: (() => void) | undefined;

// Apply iOS touch protections to every mounted game, not the game-selection page.
// Keep native clicks on buttons and selects, and pointer controls on canvas/joysticks.
const isPlaying = (): boolean => document.body.classList.contains('playing');
const editable = (target: EventTarget | null): boolean =>
  target instanceof Element && !!target.closest('input,textarea,[contenteditable="true"]');
const stopNativeGesture = (event: Event): void => {
  if (isPlaying() && !editable(event.target) && event.cancelable) event.preventDefault();
};
for (const name of ['gesturestart', 'gesturechange', 'gestureend', 'contextmenu', 'selectstart', 'dragstart']) {
  document.addEventListener(name, stopNativeGesture, { capture: true, passive: false });
}
// Safari can select text after a long press even if selectstart is canceled.
document.addEventListener('selectionchange', () => {
  if (isPlaying() && !editable(document.activeElement)) window.getSelection()?.removeAllRanges();
});
// Disable native page gestures everywhere inside games while retaining pointer events.
document.addEventListener('touchmove', (event: TouchEvent) => {
  if (isPlaying() && !editable(event.target) && event.cancelable) event.preventDefault();
}, { capture: true, passive: false });
// Stop Safari text loupe/callout on static labels, without suppressing button clicks
// or canvas/joystick pointerdown handlers.
document.addEventListener('touchstart', (event: TouchEvent) => {
  if (!isPlaying() || editable(event.target) || !(event.target instanceof Element)) return;
  if (event.target.closest('button,a,select,canvas,[role="button"],[aria-label="손가락으로 움직이는 이동 조이스틱"]')) return;
  if (event.target.matches('p,span,h1,h2,h3,h4,h5,h6,strong,b,small,label') && event.cancelable) event.preventDefault();
}, { capture: true, passive: false });


function renderHome(): void {
  disposeGame?.();
  disposeGame = undefined;
  document.body.classList.remove('playing');
  document.title = '조나단 게임방';

  app.innerHTML = `
    <main class="home-shell">
      <header class="storybook-header">
        <div class="sky-decor" aria-hidden="true"><span class="cloud cloud-a">☁</span><span class="cloud cloud-b">☁</span><span class="sun">☀</span><span class="sky-star">⭐</span></div>
        <div class="header-inner">
          <img class="header-duck" src="/assets/jonathan-chick-friends-3d.webp" alt="파란 J 모자를 쓴 병아리 조나단과 오리, 닭 친구들" />
          <div class="brand-group"><a class="storybook-brand" href="#" aria-label="조나단 게임방 홈"><span class="brand-blue">조나단</span> <span class="brand-orange">게임</span><span class="brand-green">방</span> 🎮</a>
          <p>재미있는 게임이 가득한 조나단의 게임방에 놀러오세요!</p></div>
        </div>
      </header>
      <nav class="kid-nav" aria-label="게임방 메뉴">
        <a href="#" class="nav-pill">🏠 홈</a><a href="#game-list" class="nav-pill selected">🎮 게임하기</a>
        <a href="#game-list" class="nav-pill">🏆 게임 목록</a><a href="#game-list" class="nav-pill">⭐ 즐겨찾기</a>
      </nav>
      <section class="adventure-hero" aria-label="조나단과 함께 신나는 게임 여행">
        <div class="hero-clouds" aria-hidden="true">☁️　⭐　☁️</div>
        <div class="hero-message"><span class="wood-sign">조나단과 함께<br />신나는 게임 여행을<br />떠나요!</span><a href="#game-list" class="adventure-button">🎮 게임 고르기 ↓</a></div>
        <div class="hero-scene hero-friends" aria-hidden="true"><img src="/assets/jonathan-chick-friends-3d.webp" alt="" width="1254" height="1254" fetchpriority="high" decoding="async" /></div>
      </section>
      <section class="library" id="game-list" aria-labelledby="library-title">
        <div class="section-heading"><h2 id="library-title">🎮 게임 선택하기 <span>✨</span></h2><p>조나단이 준비한 재미있는 게임을 골라보세요!</p></div>
        <div class="game-grid">
          ${games.map(gameCard).join('')}
        </div>
      </section>
      <footer>🐥 조나단 게임방 · 오늘도 신나게 놀아요! ⭐</footer>
    </main>`;

  app.querySelectorAll<HTMLButtonElement>('[data-game-id]').forEach((button) => {
    button.addEventListener('click', () => {
      const game = games.find((candidate) => candidate.id === button.dataset.gameId);
      if (game) launchGame(game);
    });
  });
}

function gameCard(game: GameDefinition): string {
  const thumbnails: Record<string, string> = {
    'block-survival': '/assets/thumbnails/block-survival.webp',
    'pet-town': '/assets/thumbnails/pet-town.webp',
    'toilet-quiz': '/assets/thumbnails/toilet-quiz.webp?v=pretty-hero-1',
    'fart-mission': '/assets/thumbnails/fart-mission.webp',
    'fart-escape': '/assets/thumbnails/fart-escape.webp',
    'ghost-battle': '/assets/thumbnails/ghost-battle.webp',
    'duck-evolution-hunter': '/assets/thumbnails/duck-evolution-hunter.webp',
    'duck-obby': '/assets/thumbnails/duck-obby.webp',
    'duck-brainrot': '/assets/thumbnails/duck-brainrot.webp',
    'duck-blocks': '/assets/thumbnails/duck-blocks.webp',
    'star-guardian': '/assets/thumbnails/star-guardian.webp',
    'monster-merge': '/assets/thumbnails/monster-merge.webp',
    'monster-island': '/assets/thumbnails/monster-island.webp',
    'giratina-raise': '/assets/thumbnails/giratina-raise.webp',
    'egg-heist-3d': '/assets/thumbnails/egg-heist-3d.webp',
    'ar-spirit-hunt': '/assets/thumbnails/ar-spirit-hunt.webp',
  };
  const thumbnail = thumbnails[game.id];
  return `
    <article class="toy-card color-blue">
      <div class="toy-illustration toy-thumbnail" aria-hidden="true">
        ${thumbnail
          ? `<img src="${thumbnail}" alt="" width="360" height="242" loading="lazy" decoding="async" onerror="this.onerror=null;this.src=&#39;/assets/thumbnails/ghost-battle.webp&#39;" />`
          : `<span class="toy-thumbnail-icon">${game.icon}</span>`}
      </div>
      <div class="toy-copy"><h3>${game.title}</h3><p>${game.description}</p>
      <button class="toy-play" type="button" data-game-id="${game.id}" aria-label="${game.title} 게임하기">▶　게임하기</button></div>
    </article>`;
}

function launchGame(game: GameDefinition): void {
  disposeGame?.();
  app.innerHTML = '<main class="play-root" aria-live="polite"></main>';
  const root = app.querySelector<HTMLElement>('.play-root');
  if (!root) return;
  document.body.classList.add('playing');
  document.title = `${game.title} · 조나단 게임방`;
  history.pushState({ game: game.id }, '', `#${game.id}`);
  disposeGame = game.mount(root, { onExit: () => history.back() });
}

window.addEventListener('popstate', renderHome);
window.addEventListener('hashchange', () => { if (!location.hash) renderHome(); });

renderHome();
